// Consolidated debt overview — /debts (manager+).
// Two panels: customers who owe us, and suppliers we owe.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Users, Truck } from "lucide-react";
import { apiGet, apiClient } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";
import { MoneyInput } from "@/components/ui/money-input";
import { useAuthStore } from "@/lib/auth/store";
import { SupplierLedgerModal } from "./supplier-ledger-modal";

interface DebtorRow {
  customerId: string;
  name: string;
  phone: string;
  balance: string;
  earliestDue?: string | null;
}

interface Supplier {
  id: string;
  name: string;
  phone: string;
  debt: string;
  isActive: boolean;
}

function fmtVND(n: string) {
  return new Intl.NumberFormat("vi-VN").format(parseFloat(n) || 0) + " ₫";
}

// Dialog trả nợ NCC (không gắn phiếu nhập) — ghi sổ chi + trừ công nợ.
function PaySupplierDialog({ supplier, onClose }: { supplier: Supplier; onClose: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<"cash" | "bank_manual">("cash");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const debt = parseFloat(supplier.debt) || 0;

  const payMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/v1/suppliers/${supplier.id}/payments`, {
        amount: parseFloat(amount),
        method,
        note,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      onClose();
    },
    onError: (e) => setError(translateApiError(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md space-y-3 rounded-lg border bg-background p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-semibold">Trả nợ NCC — {supplier.name}</h2>
        <p className="text-sm text-muted-foreground">
          Công nợ hiện tại: <span className="font-semibold text-amber-600">{fmtVND(supplier.debt)}</span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <MoneyInput value={amount} onChange={setAmount} placeholder="Số tiền trả" className="w-40" />
          <Button type="button" variant="outline" size="sm" onClick={() => setAmount(String(Math.round(debt)))}>
            Trả hết
          </Button>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as "cash" | "bank_manual")}
            className="h-9 rounded-md border bg-background px-2 text-sm"
          >
            <option value="cash">Tiền mặt</option>
            <option value="bank_manual">Chuyển khoản</option>
          </select>
        </div>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Ghi chú (tùy chọn)"
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Khoản chi sẽ trừ công nợ NCC và hiện trong báo cáo Quỹ tiền (chiều CHI).
        </p>
        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Hủy</Button>
          <Button
            size="sm"
            disabled={payMutation.isPending || !amount || parseFloat(amount) <= 0}
            onClick={() => payMutation.mutate()}
          >
            {payMutation.isPending ? "Đang ghi…" : "Ghi chi trả nợ"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function sumBalance(nums: string[]) {
  return nums.reduce((acc, n) => acc + (parseFloat(n) || 0), 0);
}

export function DebtOverviewPage() {
  const [paySupplier, setPaySupplier] = useState<Supplier | null>(null);
  const [ledgerSupplierId, setLedgerSupplierId] = useState<string | null>(null);
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");
  // Viewer (kế toán) xem sổ được nhưng không trả nợ — ẩn nút ghi thay vì để 403.
  const canPay = useAuthStore((s) => s.user)?.role !== "viewer";
  // ⚠️ PHẢI hiện lỗi khi query chết — trước đây lỗi bị nuốt, bảng hiện
  // "Không có khách nào đang nợ 🎉" giả (sự cố 19-20/09: BE 500 mà kế toán
  // tưởng hết nợ / "không xem được").
  const { data: debtors = [], isLoading: loadingC, error: errorC } = useQuery({
    queryKey: ["debts", "customers"],
    queryFn: () => apiGet<DebtorRow[]>("/v1/debts/customers"),
  });

  const { data: suppliers = [], isLoading: loadingS, error: errorS } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => apiGet<Supplier[]>("/v1/suppliers"),
  });

  // Suppliers with a non-zero payable balance (we owe them).
  const owedSuppliers = (suppliers ?? [])
    .filter((s) => (parseFloat(s.debt) || 0) > 0)
    .sort((a, b) => (parseFloat(b.debt) || 0) - (parseFloat(a.debt) || 0));

  // Lọc client-side theo tên/SĐT — danh sách nợ trả về trọn gói, không phân trang.
  const fc = filterCustomer.trim().toLowerCase();
  const visibleDebtors = fc
    ? debtors.filter(
        (d) => (d.name || "").toLowerCase().includes(fc) || (d.phone || "").includes(fc),
      )
    : debtors;
  const fs = filterSupplier.trim().toLowerCase();
  const visibleSuppliers = fs
    ? owedSuppliers.filter(
        (s) => (s.name || "").toLowerCase().includes(fs) || (s.phone || "").includes(fs),
      )
    : owedSuppliers;

  const totalCustomerDebt = sumBalance(visibleDebtors.map((d) => d.balance));
  const totalSupplierDebt = sumBalance(visibleSuppliers.map((s) => s.debt));

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Công nợ</h1>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Khách nợ mình */}
        <section className="rounded-lg border">
          <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-4 py-3">
            <div className="flex items-center gap-2 font-semibold">
              <Users size={18} className="text-pink-600 dark:text-pink-400" />
              Khách nợ mình
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-destructive">{fmtVND(String(totalCustomerDebt))}</div>
              <div className="text-xs text-muted-foreground">{visibleDebtors.length} khách</div>
            </div>
          </div>
          <div className="border-b px-4 py-2">
            <input
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
              placeholder="Lọc theo tên hoặc SĐT khách…"
              className="h-9 w-full max-w-xs rounded-md border bg-background px-2 text-sm"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/20 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Khách hàng</th>
                  <th className="px-4 py-2 text-left">SĐT</th>
                  <th className="px-4 py-2 text-right">Số nợ</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {loadingC && (
                  <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">Đang tải…</td></tr>
                )}
                {!loadingC && errorC && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center">
                      <span className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        Không tải được danh sách khách nợ: {translateApiError(errorC)} — thử F5, nếu vẫn lỗi báo quản trị.
                      </span>
                    </td>
                  </tr>
                )}
                {!loadingC && !errorC && visibleDebtors.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center font-medium text-green-600">
                      {fc ? "Không có khách nợ khớp bộ lọc" : "Không có khách nào đang nợ 🎉"}
                    </td>
                  </tr>
                )}
                {visibleDebtors.map((r) => (
                  <tr key={r.customerId} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2 font-medium">{r.name || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.phone || "—"}</td>
                    <td className="px-4 py-2 text-right font-bold text-destructive tabular-nums">{fmtVND(r.balance)}</td>
                    <td className="px-4 py-2 text-right">
                      <Link to={`/customers/${r.customerId}` as any} className="text-primary hover:underline text-xs">
                        Chi tiết
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Mình nợ NCC */}
        <section className="rounded-lg border">
          <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-4 py-3">
            <div className="flex items-center gap-2 font-semibold">
              <Truck size={18} className="text-amber-600 dark:text-amber-400" />
              Mình nợ nhà cung cấp
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-amber-600 dark:text-amber-400">{fmtVND(String(totalSupplierDebt))}</div>
              <div className="text-xs text-muted-foreground">{visibleSuppliers.length} NCC</div>
            </div>
          </div>
          <div className="border-b px-4 py-2">
            <input
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              placeholder="Lọc theo tên hoặc SĐT NCC…"
              className="h-9 w-full max-w-xs rounded-md border bg-background px-2 text-sm"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/20 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Nhà cung cấp</th>
                  <th className="px-4 py-2 text-left">SĐT</th>
                  <th className="px-4 py-2 text-right">Công nợ</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {loadingS && (
                  <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">Đang tải…</td></tr>
                )}
                {!loadingS && errorS && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center">
                      <span className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        Không tải được danh sách NCC: {translateApiError(errorS)} — thử F5, nếu vẫn lỗi báo quản trị.
                      </span>
                    </td>
                  </tr>
                )}
                {!loadingS && !errorS && visibleSuppliers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center font-medium text-green-600">
                      {fs ? "Không có NCC khớp bộ lọc" : "Không nợ NCC nào 🎉"}
                    </td>
                  </tr>
                )}
                {visibleSuppliers.map((s) => (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2 font-medium">{s.name || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{s.phone || "—"}</td>
                    <td className="px-4 py-2 text-right font-bold text-amber-600 dark:text-amber-400 tabular-nums">{fmtVND(s.debt)}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setLedgerSupplierId(s.id)}
                        className="mr-3 text-xs font-medium text-primary hover:underline"
                        title="Xem chi tiết: nhập hàng ngày nào, trả tiền ngày nào"
                      >
                        Chi tiết
                      </button>
                      {canPay && (
                        <button
                          type="button"
                          onClick={() => setPaySupplier(s)}
                          className="mr-3 text-xs font-medium text-green-700 hover:underline dark:text-green-400"
                        >
                          Trả nợ
                        </button>
                      )}
                      <Link to={"/admin/suppliers" as any} className="text-primary hover:underline text-xs">
                        Quản lý
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {paySupplier && (
        <PaySupplierDialog supplier={paySupplier} onClose={() => setPaySupplier(null)} />
      )}

      {ledgerSupplierId && (
        <SupplierLedgerModal
          supplierId={ledgerSupplierId}
          onClose={() => setLedgerSupplierId(null)}
        />
      )}
    </div>
  );
}
